import { useEffect, useRef, useState } from 'react';
import classes from './PreJoin.module.css';
import { ReportsPageHeader } from '../../Headers/ReportsPageHeader';
import PrimaryButtonV2 from '../Buttons/PrimaryButtonV2';
import calendarLarge from '../../../assets/icons/calendar-large.svg';
import microphoneIcon from '../../../assets/icons/microphoneOn.svg';
import microphoneIconMuted from '../../../assets/icons/microphoneOff.svg';
import videoIcon from '../../../assets/icons/cameraOn.svg';
import videoIconStopped from '../../../assets/icons/cameraOff.svg';
import moment from 'moment';
import { client } from '../../../networking/client';
import { useLocation, useNavigate } from 'react-router-dom';
import eyeson from 'eyeson';
import { useCountdown } from '../../../hooks/useCountdown';
import { useTranslation } from '../../../hooks/useTranslations';

const bellSound = require('../../../assets/audio/bell.mp3');

const PreJoinScreen = () => {
  const location = useLocation();
  const [pageInfo] = useState<any>(location.state);
  const { translations } = useTranslation();
  const navigate = useNavigate();
  const videoLocal = useRef<HTMLVideoElement>(null);
  const [eyesonGuestToken, setEyesonGuestToken] = useState<string | undefined>();
  const audio = new Audio(bellSound);
  const [days, hours, minutes, seconds] = useCountdown(pageInfo.sales_order_appointment.start);
  const [stream] = useState<MediaStream | null>(null);
  const lang = localStorage.getItem('lang') || 'en';

  useEffect(() => {
    const getMedia = async () => {
      navigator.mediaDevices
        .getUserMedia({ video: { width: 300 } })
        .then((stream) => {
          let video = videoLocal.current;
          if (video) {
            video.srcObject = stream;
            video.play();
          }
        })
        .catch((err) => {
          console.error('error:', err);
        });
    };

    getMedia();

    return () => {
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [videoLocal]);

  useEffect(() => {
    let timer = setInterval(() => {
      getEyesonGuest();
      if (eyesonGuestToken) {
        clearInterval(timer);
      }
    }, 3000);

    const getEyesonGuest = async () => {
      try {
        const appointmentInfoResponse: any = await client.get(
          `/healthpoint/sales-order-appointment/${pageInfo?.sales_order_appointment.sales_order_appointment_id}`
        );
        if (appointmentInfoResponse?.data?.salesOrderAppointment?.guest_token) {
          audio.play();
          setEyesonGuestToken(appointmentInfoResponse?.data?.salesOrderAppointment?.guest_token);
        }
      } catch (_) {}
    };

    return () => clearInterval(timer);
  }, [eyesonGuestToken, pageInfo]);
  const [hasAudio, setHasAudio] = useState<boolean>(true);
  const [hasVideo, setHasVideo] = useState<boolean>(true);
  const [skip] = useState<boolean>(true);
  const toggleAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioTrack = stream.getAudioTracks()[0];

      if (!audioTrack) {
        console.error('No audio track found.');
        return;
      }
      audioTrack.enabled = !audioTrack.enabled;
      setHasAudio(!hasAudio);
      if (!audioTrack.enabled) {
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        alert('Audio access is required to use this feature. Please grant audio access.');
      } else {
        console.log('error', error);
      }
    }
  };

  const toggleVideo = async () => {
    let video = videoLocal.current;

    if (!video) {
      return;
    }
    try {
      if (video.paused) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        video.srcObject = stream;
        video.play();
        setHasVideo(true);
      } else {
        video.pause();
        setHasVideo(false);
        if (video.srcObject) {
          const stream = video.srcObject as any;
          const tracks = stream.getTracks();
          tracks.forEach((track: { stop: () => any }) => track.stop());
          video.srcObject = null;
        }
      }
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        alert('Camera access is required to use this feature. Please grant camera access.');
      } else {
        console.log('error', error);
      }
    }
  };

  useEffect(() => {
    if (!skip) {
      eyeson.send({
        type: 'change_stream',
        audio: hasAudio,
        video: hasVideo,
      });
    }
  }, [hasAudio, hasVideo, skip]);

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <ReportsPageHeader selectedSalesOrder={pageInfo} onBackButtonClick={() => {}} />
      </div>
      <div className={classes.wrapper}>
        <div className={classes.header}>{translations('video.consultation')}</div>
        <div className={classes.mainContainer}>
          <div className={classes.cameraContainer}>
            <div className={classes.localVideoContainer}>
              <video
                id="videolocal"
                className={classes.localVideo}
                playsInline
                autoPlay
                muted
                ref={videoLocal}
                style={hasVideo ? {} : { background: '#000' }}
              ></video>
              <div className={classes.videoControls}>
                <img
                  src={hasAudio ? microphoneIcon : microphoneIconMuted}
                  className={classes.callIcon}
                  alt="microphoneIcon"
                  onClick={(e) => toggleAudio()}
                />

                <img
                  src={hasVideo ? videoIcon : videoIconStopped}
                  className={classes.callIcon}
                  alt="videoIcon"
                  onClick={(e) => toggleVideo()}
                />
              </div>
            </div>
            <div className={classes.appointmentContainer}>
              <img src={calendarLarge} alt={'calendar-large'} className={classes.calendarIcon} />
              <div className={classes.appointmentTitle}>{translations('your.appointment')}</div>
              <div className={classes.appointmentDate}>
                {moment(pageInfo?.sales_order_appointment.start)
                  .locale(lang)
                  .format(' DD.MMMM.YYYY, HH:mmA')}
              </div>
              <div className={classes.appointmentDescription}>
                {translations('please.enter.room')}
              </div>
              <div className={classes.dmsContainer}>
                {days + hours + minutes + seconds < 0 ? (
                  <h2>{translations('label.consultation_started')}</h2>
                ) : (
                  <div>
                    <span>{translations('label.video_consultation_starts_in')}</span>
                    <h1>{`${
                      days !== 0 ? (days === 1 ? `${days} Day and` : `${days} Days and`) : ''
                    } ${hours}:${minutes}:${seconds}`}</h1>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* <div className={classes.callSettingsContainer}>
            <div className={classes.callSettingsCard}>
              <img className={classes.callSettingsIcon} src={microphone} alt={'mic'} />
              <div>Microphone (Realte...</div>
            </div>
            <div className={classes.callSettingsCard}>
              <img className={classes.callSettingsIcon} src={speaker} alt={'mic'} />
              <div>Speakers (Realtek...</div>
            </div>
            <div
              className={classes.callSettingsCard}
              onClick={() => {
                videoLocal.current!.pause();
              }}
            >
              <img className={classes.callSettingsIcon} src={camera} alt={'mic'} />
              <div>Integrated Camera</div>
            </div>
          </div> */}
          <div className={classes.buttonContainer}>
            <PrimaryButtonV2
              text={translations('join.the.call')}
              containerStyle={{}}
              buttonStyle={{
                margin: '30px 0px',
                textTransform: 'uppercase',
                width: 260,
                height: 50,
                borderRadius: 7,
                fontSize: 16,
              }}
              disabled={!eyesonGuestToken}
              onClick={() => {
                navigate('/web/guest-video-consulting-room', {
                  state: {
                    salesOrder: { ...pageInfo },
                    roomData: {},
                    guestToken: eyesonGuestToken,
                  },
                });
              }}
            />
          </div>
          {/* <div className={classes.addDocumentsButton}>
            <img src={paperClip} alt={'clip'} />
            <div className={classes.addDocumentsText}>Add documents</div>
            <div className={classes.addDocumentsTextUnderline}>1 File</div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export { PreJoinScreen };
